// src/utils/platform.js
// Platform detection + singleton cache.
// - getPlatform(): stable snapshot (recommended everywhere)
// - detectPlatform(): raw detector (only use if you truly need re-check)

export const detectPlatform = () => {
  const w = typeof window !== 'undefined' ? window : undefined
  const n = typeof navigator !== 'undefined' ? navigator : undefined

  const ua = n && n.userAgent ? String(n.userAgent) : ''
  const uaLower = ua.toLowerCase()

  // ---- OS ----
  const isAndroid = /android/i.test(uaLower)
  const isIOS = /iphone|ipad|ipod/i.test(uaLower)

  // ---- PWA / standalone ----
  const isStandaloneDisplayMode = (() => {
    try {
      return !!(w && w.matchMedia && w.matchMedia('(display-mode: standalone)').matches)
    } catch (_) {
      return false
    }
  })()

  const isIOSStandalone = (() => {
    try {
      return !!(n && Object.prototype.hasOwnProperty.call(n, 'standalone') && n.standalone)
    } catch (_) {
      return false
    }
  })()

  const isPwaStandalone = isStandaloneDisplayMode || isIOSStandalone

  // ---- iOS browser identity ----
  const isCriOS = /crios/i.test(uaLower)
  const isFxiOS = /fxios/i.test(uaLower)
  const isEdgiOS = /edgios/i.test(uaLower)
  const isOPiOS = /opios/i.test(uaLower)

  const isSafari = (() => {
    if (!isIOS) return false
    const hasSafari = /safari/i.test(uaLower)
    if (!hasSafari) return false
    if (isCriOS || isFxiOS || isEdgiOS || isOPiOS) return false
    return true
  })()

  // ---- Android browser brands (informational) ----
  const isSamsungInternet = /samsungbrowser\/\d+/i.test(uaLower)
  const isAndroidEdge = /edga\/\d+/i.test(uaLower)

  // ---- WebView heuristics ----
  // IMPORTANT: do NOT depend on custom bridges (like window.OneTimeJobBridge).
  // WebView should be recognized by UA signals, so old app builds still behave correctly.
  const isAndroidWebView = (() => {
    if (!isAndroid) return false
    if (/\bwv\b/i.test(ua)) return true
    if (/(; wv\))/i.test(ua)) return true
    if (/version\/\d+(\.\d+)?/i.test(uaLower)) return true
    return false
  })()

  const isIOSWebView = (() => {
    if (!isIOS) return false
    const hasAppleWebKit = /applewebkit/i.test(uaLower)
    const hasSafariToken = /safari/i.test(uaLower)
    if (hasAppleWebKit && !hasSafariToken) return true
    if (isSafari) return false
    return false
  })()

  const isWebView = isAndroidWebView || isIOSWebView
  const isTabBrowser = !isWebView && !isPwaStandalone
  const isBrowser = !isWebView
  const isAndroidBrowser = isAndroid && isBrowser
  const isDesktop = !isAndroid && !isIOS

  return {
    ua,

    // OS
    isAndroid,
    isIOS,

    // Runtime
    isAndroidWebView,
    isIOSWebView,
    isWebView,
    isBrowser,
    isTabBrowser,
    isPwaStandalone,

    // Browsers (info)
    isSafari,
    isSamsungInternet,
    isAndroidEdge,

    // Convenience
    isAndroidBrowser,
    isDesktop,
  }
}

// ---- Singleton cache ----
let _cached = null

export const getPlatform = () => {
  if (_cached) return _cached
  _cached = Object.freeze(detectPlatform())
  return _cached
}